import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, Shield, Database, Cookie, Eye, Lock, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const PoliticaPrivacidade = () => {
  const { siteSettings } = useContext(AuthContext);
  const siteName = siteSettings?.site_name || 'Nossa Feira';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Política de Privacidade</h1>
              <p className="text-gray-600">Última atualização: Outubro de 2024</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          
          {/* Introduction */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-600" />
                1. Introdução
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <p>
                A privacidade dos nossos usuários é fundamental para o {siteName}. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza nossa plataforma.
              </p>
              <p>
                Ao utilizar o {siteName}, você concorda com as práticas descritas nesta política. Se você não concordar com qualquer parte desta política, não deve usar nossos serviços.
              </p>
            </CardContent>
          </Card>

          {/* Data Collection */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                2. Informações que Coletamos
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>2.1 Informações Fornecidas por Você</h4>
              <ul>
                <li><strong>Dados de Cadastro:</strong> Nome, telefone/WhatsApp, email, cidade</li>
                <li><strong>Informações de Produtos:</strong> Para produtores - descrições, preços, localização</li>
                <li><strong>Avaliações:</strong> Comentários e notas sobre produtos e produtores</li>
                <li><strong>Comunicações:</strong> Mensagens enviadas através de nossos canais de suporte</li>
              </ul>
              
              <h4>2.2 Informações Coletadas Automaticamente</h4>
              <ul>
                <li><strong>Dados de Uso:</strong> Páginas visitadas, tempo de permanência, cliques</li>
                <li><strong>Informações do Dispositivo:</strong> Tipo de dispositivo, navegador, sistema operacional</li>
                <li><strong>Dados de Localização:</strong> Informações gerais de localização (cidade/região)</li>
                <li><strong>Cookies:</strong> Para melhorar a experiência do usuário</li>
              </ul>

              <h4>2.3 Informações de Terceiros</h4>
              <ul>
                <li><strong>WhatsApp:</strong> Quando você clica para contatar produtores</li>
                <li><strong>Navegadores:</strong> Informações básicas do navegador e preferências</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Usage */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-600" />
                3. Como Usamos suas Informações
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>3.1 Prestação de Serviços</h4>
              <ul>
                <li>Criar e gerenciar sua conta de usuário</li>
                <li>Facilitar conexões entre produtores e consumidores</li>
                <li>Processar e exibir avaliações de produtos</li>
                <li>Fornecer suporte ao cliente</li>
              </ul>

              <h4>3.2 Comunicação</h4>
              <ul>
                <li>Enviar notificações importantes sobre sua conta</li>
                <li>Responder a suas solicitações e dúvidas</li>
                <li>Informar sobre atualizações da plataforma</li>
              </ul>

              <h4>3.3 Melhorias e Segurança</h4>
              <ul>
                <li>Analisar o uso da plataforma para melhorias</li>
                <li>Detectar e prevenir fraudes ou atividades suspeitas</li>
                <li>Cumprir obrigações legais e regulamentares</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-600" />
                4. Compartilhamento de Informações
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>4.1 Entre Usuários da Plataforma</h4>
              <p>
                Algumas informações são compartilhadas para facilitar as conexões:
              </p>
              <ul>
                <li><strong>Produtores:</strong> Nome, localização, produtos, avaliações</li>
                <li><strong>Consumidores:</strong> Nome e avaliações (quando publicadas)</li>
              </ul>

              <h4>4.2 Não Compartilhamos com Terceiros</h4>
              <p>
                Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins comerciais, exceto:
              </p>
              <ul>
                <li>Com seu consentimento explícito</li>
                <li>Para cumprir obrigações legais</li>
                <li>Para proteger nossos direitos e segurança</li>
              </ul>

              <h4>4.3 WhatsApp</h4>
              <p>
                Quando você clica para contatar um produtor, você será redirecionado para o WhatsApp. Essa comunicação está sujeita aos termos e política de privacidade do WhatsApp.
              </p>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-yellow-600" />
                5. Cookies e Tecnologias Similares
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>5.1 O que são Cookies</h4>
              <p>
                Cookies são pequenos arquivos de texto armazenados em seu dispositivo que nos ajudam a melhorar sua experiência na plataforma.
              </p>

              <h4>5.2 Como Usamos Cookies</h4>
              <ul>
                <li><strong>Cookies Essenciais:</strong> Necessários para o funcionamento básico da plataforma</li>
                <li><strong>Cookies de Performance:</strong> Ajudam-nos a entender como você usa a plataforma</li>
                <li><strong>Cookies de Funcionalidade:</strong> Lembram suas preferências e configurações</li>
              </ul>

              <h4>5.3 Gerenciamento de Cookies</h4>
              <p>
                Você pode gerenciar cookies através das configurações do seu navegador. Note que desabilitar cookies pode afetar algumas funcionalidades da plataforma.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>6. Segurança dos Dados</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>6.1 Medidas de Proteção</h4>
              <ul>
                <li>Criptografia de dados sensíveis</li>
                <li>Acesso restrito às informações pessoais</li>
                <li>Monitoramento regular de segurança</li>
                <li>Backups seguros e regulares</li>
              </ul>

              <h4>6.2 Senhas</h4>
              <p>
                Suas senhas são armazenadas de forma criptografada. Nunca compartilhamos ou solicitamos sua senha por email ou telefone.
              </p>
            </CardContent>
          </Card>

          {/* User Rights */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>7. Seus Direitos</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <p>Você tem direito a:</p>
              <ul>
                <li><strong>Acesso:</strong> Solicitar acesso às suas informações pessoais</li>
                <li><strong>Correção:</strong> Corrigir informações incorretas ou desatualizadas</li>
                <li><strong>Exclusão:</strong> Solicitar a exclusão de suas informações</li>
                <li><strong>Portabilidade:</strong> Receber suas informações em formato estruturado</li>
                <li><strong>Oposição:</strong> Opor-se ao processamento de suas informações</li>
              </ul>

              <h4>7.1 Como Exercer seus Direitos</h4>
              <p>
                Para exercer qualquer um desses direitos, entre em contato conosco através do email {siteSettings?.contact_email || 'contato@nossafeira.com'} ou telefone {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}.
              </p>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>8. Retenção de Dados</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <p>
                Mantemos suas informações pessoais apenas pelo tempo necessário para:
              </p>
              <ul>
                <li>Fornecer nossos serviços</li>
                <li>Cumprir obrigações legais</li>
                <li>Resolver disputas</li>
                <li>Fazer cumprir nossos acordos</li>
              </ul>

              <p>
                Quando você excluir sua conta, removeremos suas informações pessoais, exceto aquelas que devemos manter por obrigação legal.
              </p>
            </CardContent>
          </Card>

          {/* Changes and Contact */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>9. Alterações e Contato</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>9.1 Alterações nesta Política</h4>
              <p>
                Podemos atualizar esta política ocasionalmente. Notificaremos sobre mudanças significativas através da plataforma ou email.
              </p>

              <h4>9.2 Entre em Contato</h4>
              <p>
                Para dúvidas sobre esta política ou sobre como tratamos seus dados:
              </p>
              <ul>
                <li><strong>Email:</strong> {siteSettings?.contact_email || 'contato@nossafeira.com'}</li>
                <li><strong>Telefone:</strong> {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}</li>
                <li><strong>Endereço:</strong> {siteSettings?.contact_address || 'Bom Jesus da Lapa, BA'}</li>
              </ul>

              <h4>9.3 Legislação Aplicável</h4>
              <p>
                Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD) - Lei 13.709/2018 e demais legislações brasileiras aplicáveis.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 p-6 bg-blue-50 rounded-lg">
          <p className="text-center text-gray-600 text-sm">
            Sua privacidade é importante para nós. Leia também nossos{' '}
            <Link to="/termos-de-uso" className="text-blue-600 hover:underline">
              Termos de Uso
            </Link>
            {' '}para entender completamente como funciona o {siteName}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PoliticaPrivacidade;